import { useState } from 'react'
import { useLocation, useNavigate, Outlet, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { usePageTitle } from '@/hooks/use-page-title'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/shadcn/button'
import { Database, Search, Download } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb'
import { UsageDisplay } from '@/components/UsageDisplay'

export default function MemoryPage() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useIsMobile()
  
  // 设置页面标题
  useDocumentTitle(t('memory.title'))
  
  // 在移动端设置页面标题到顶栏
  usePageTitle('memory.title')
  
  // 获取当前语言前缀
  const getCurrentLanguagePrefix = () => {
    const path = location.pathname
    if (path.startsWith('/en')) return '/en'
    if (path.startsWith('/zh')) return '/zh'
    return '/zh' // 默认中文
  }
  
  const languagePrefix = getCurrentLanguagePrefix()
  
  // 根据当前路径确定活动标签
  const getActiveTab = () => {
    const path = location.pathname
    if (path.includes('/memories/detail')) return 'detail'
    if (path.includes('/memories/export')) return 'export'
    if (path.includes('/memories/search')) return 'search'
    return 'libraries'
  }
  
  const [activeTab, setActiveTab] = useState(getActiveTab())
  
  // 获取当前记忆库ID
  const currentMemoryId = searchParams.get('memory_id')
  
  // 生成面包屑
  const getBreadcrumbItems = () => {
    const items = [
      {
        label: t('memory.breadcrumb.memory'),
        href: `${languagePrefix}/dashboard/memories`
      }
    ]
    
    // 根据当前路径添加相应的面包屑项
    const path = location.pathname
    
    if (path.includes('/memories/detail') && currentMemoryId) {
      items.push({
        label: currentMemoryId,
        href: `${languagePrefix}/dashboard/memories/detail?memory_id=${encodeURIComponent(currentMemoryId)}`
      })
    } else if (path.includes('/memories/export')) {
      items.push({
        label: t('memory.breadcrumb.exportMemory'),
        href: `${languagePrefix}/dashboard/memories/export`
      })
    } else if (path.includes('/memories/search')) {
      items.push({
        label: t('memory.breadcrumb.memorySearch'),
        href: `${languagePrefix}/dashboard/memories/search`
      })
    }
    
    return items
  }

  const tabs = [
    { 
      id: 'libraries', 
      label: t('memory.tabs.vaults'), 
      icon: Database,
      path: `${languagePrefix}/dashboard/memories`
    },
    { 
      id: 'search', 
      label: t('memory.tabs.search'), 
      icon: Search,
      path: `${languagePrefix}/dashboard/memories/search`,
      disabled: true
    },
    { 
      id: 'export', 
      label: t('memory.tabs.export'), 
      icon: Download,
      path: `${languagePrefix}/dashboard/memories/export`
    },
  ]

  const handleTabClick = (tab: typeof tabs[0]) => {
    if (tab.disabled) return
    setActiveTab(tab.id)
    navigate(tab.path)
  }

  return (
    <div className={`space-y-2 ${isMobile ? 'pt-2' : 'pt-8'}`}>
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          {getBreadcrumbItems().map((item, index) => (
            <div key={index} className="flex items-center">
              <BreadcrumbItem>
                {index === getBreadcrumbItems().length - 1 ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.href}>{item.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < getBreadcrumbItems().length - 1 && <BreadcrumbSeparator />}
            </div>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex flex-col items-stretch space-y-0 sm:flex-row mt-0">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <h1 className={`text-2xl font-semibold tracking-tight ${isMobile ? 'hidden' : ''}`}>{t('memory.title')}</h1>
          <p className="text-sm text-muted-foreground  mb-2">
            {t('memory.description')}
          </p>
        </div>
        <div className="flex items-center mt-4 sm:mt-0">
          <UsageDisplay />
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8">
        <div className="flex flex-wrap gap-2 border-t border-b border-dashed py-4 px-4 sm:px-6 lg:px-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            const isDisabled = tab.disabled
            
            return (
              <Button
                key={tab.id}
                variant="ghost"
                size="sm"
                onClick={() => handleTabClick(tab)}
                disabled={isDisabled}
                className={`flex items-center gap-2 rounded-full px-4 py-2 transition-colors ${
                  isActive
                    ? 'bg-gray-100 text-black hover:bg-gray-200'
                    : isDisabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:text-black hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </Button>
            )
          })}
        </div>
      </div>

      {/* Tab Content - This will be handled by routing */}
      <div className="min-h-[400px]">
        <Outlet />
      </div>
    </div>
  )
}
